'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppNav from '@/components/AppNav';

import HowItWorks from '@/app/components/HowItWorks';

import SafetySection from '@/app/components/SafetySection';
import SafetyAndFooter from '@/components/SafetyAndFooter';
import { createClient } from '@/lib/supabase/client';
import { jsnService } from '@/lib/supabase/jsnService';
import { prefetchDiscoverData } from '@/lib/discoverPrefetch';
import type { UserProfile, MemberProfile } from '@/lib/supabase/types';
import { Compass, Heart, MessageCircle, Crown, HelpCircle, Shield, ArrowRight, Loader2, AlertCircle, Upload, X, Sparkles, CheckCircle,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const PREMIUM_POPUP_KEY = 'jsn_premium_popup_dismissed_date';

function shouldShowPremiumPopup(): boolean {
  if (typeof window === 'undefined') return false;
  const dismissed = localStorage.getItem(PREMIUM_POPUP_KEY);
  if (!dismissed) return true;
  const today = new Date().toISOString().slice(0, 10);
  return dismissed !== today;
}

function dismissPremiumPopupForDay(): void {
  if (typeof window === 'undefined') return;
  const today = new Date().toISOString().slice(0, 10);
  localStorage.setItem(PREMIUM_POPUP_KEY, today);
}

// Member-facing messages mapped to each admin rejection reason
const REJECTION_REASON_MESSAGES: Record<string, string> = {
  'Face is not clearly visible': 'Please upload a clear photo where your full face is easy to see.',
  'Photo is too distant or poorly framed': 'Please upload a closer head-and-shoulders photo where your face is centered and clearly visible.',
  'Photo is blurry or low quality': 'Please upload a clearer, higher-quality photo where your face can be easily identified.',
  'Photo includes more than one person': 'Please upload a photo of yourself only. Group or couple photos cannot be approved.',
  'Face is covered or obscured': 'Please upload a photo where your face is not hidden or obscured.',
  'Face is cropped out of the photo': 'Please upload a photo where your full face is visible within the frame. Photos that cut off the face cannot be approved.',
  'Photo contains nudity or sexually suggestive content': 'Please upload a respectful profile photo. Nude, partially nude, or sexually suggestive photos are not permitted on JSN.',
  'Photo is not suitable for a profile image': 'Please upload a clear, recent head-and-shoulders photo suitable for your JSN profile.',
  'Photo does not meet JSN photo standards': 'Please upload a clear, recent, close-up photo showing your full face. Head-and-shoulders photos are strongly recommended.',
};

function getMemberMessage(reason: string | null | undefined): string {
  if (!reason) return "Please upload a replacement photo that meets JSN's photo standards.";
  return REJECTION_REASON_MESSAGES[reason] || "Please upload a replacement photo that meets JSN's photo standards.";
}

export default function HomeScreen() {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [memberProfile, setMemberProfile] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showPremiumPopup, setShowPremiumPopup] = useState(false);
  const [showEmailVerifiedBanner, setShowEmailVerifiedBanner] = useState(false);
  const searchParams = useSearchParams();

  useEffect(() => {
    // Show email verified success banner if redirected from callback
    if (searchParams.get('email_verified') === '1') {
      setShowEmailVerifiedBanner(true);
      // Auto-dismiss after 8 seconds
      const t = setTimeout(() => setShowEmailVerifiedBanner(false), 8000);
      return () => clearTimeout(t);
    }
  }, [searchParams]);

  useEffect(() => {
    const load = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        window.location.href = '/sign-up-login-screen';
        return;
      }

      // Email verification gate — redirect unverified users
      if (!user.email_confirmed_at) {
        window.location.href = '/verify-email';
        return;
      }

      const [up, mp] = await Promise.all([
        jsnService.getUserProfileById(user.id),
        jsnService.getCurrentMemberProfile(),
      ]);

      // ── TEMPORARY RUNTIME LOGGING — remove after root cause confirmed ──
      console.log('[JSN Photo Gate] HomeScreen — memberProfile raw value:', mp);
      console.log('[JSN Photo Gate] HomeScreen — memberProfile is null?', mp === null);
      console.log('[JSN Photo Gate] HomeScreen — photoStatus:', mp?.photoStatus ?? '(mp is null/undefined)');
      console.log('[JSN Photo Gate] HomeScreen — userProfile.role:', up?.role);
      console.log('[JSN Photo Gate] HomeScreen — userProfile.accountStatus:', up?.accountStatus);
      // ──────────────────────────────────────────────────────────────────

      // ── Photo-review gate ──────────────────────────────────────────────
      // Block access to the full member dashboard until the profile photo is
      // approved AND the account is active. This covers photo_status values:
      // 'pending', 'rejected', 'none' (no photo uploaded).
      // FAIL-CLOSED: if mp is null (no row, RLS block, or query failure),
      // treat it the same as pending — block access to /home.
      // Admins bypass this gate. /discover-page renders the existing
      // "Photo Pending Review" UI when photoStatus is pending.
      const isAdmin = up?.role === 'admin';
      if (!isAdmin && (!mp || mp.photoStatus !== 'approved')) {
        // If the user just verified their email, show the banner briefly
        // before redirecting so they see the "Email Verified!" confirmation.
        const isEmailVerifiedRedirect = new URLSearchParams(window.location.search).get('email_verified') === '1';
        if (isEmailVerifiedRedirect) {
          // Show banner for 2 seconds then redirect to the pending review screen
          setTimeout(() => {
            window.location.href = '/discover-page';
          }, 2000);
        } else {
          window.location.href = '/discover-page';
        }
        return;
      }

      setUserProfile(up);
      setMemberProfile(mp);
      setLoading(false);

      // Show rejection modal if photo is rejected and has a reason
      if (mp?.photoStatus === 'rejected' && mp?.photoRejectionReason) {
        setShowRejectionModal(true);
      }

      // Show Premium upgrade popup for eligible free members (once per day)
      const isPremium = up?.membershipTier === 'premium';
      const photoOk = mp?.photoStatus === 'approved';
      if (!isAdmin && !isPremium && photoOk && shouldShowPremiumPopup()) {
        setShowPremiumPopup(true);
      }

      // Non-blocking background prefetch of Discover data.
      prefetchDiscoverData(user.id);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-primary" />
      </div>
    );
  }

  const displayName = userProfile?.displayName || 'Member';
  const rejectionReason = memberProfile?.photoRejectionReason;
  const memberMessage = getMemberMessage(rejectionReason);

  const quickLinks = [
    {
      key: 'discover',
      label: 'Browse Profiles',
      sublabel: 'Discover Kenyan singles',
      href: '/discover-page',
      icon: Compass,
      primary: true,
    },
    {
      key: 'matches',
      label: 'Matches',
      sublabel: 'See your mutual matches',
      href: '/matches',
      icon: Heart,
      primary: false,
    },
    {
      key: 'messages',
      label: 'Messages',
      sublabel: 'Your conversations',
      href: '/messages',
      icon: MessageCircle,
      primary: false,
    },
    {
      key: 'membership',
      label: 'Membership',
      sublabel: 'Upgrade to Premium',
      href: '/membership',
      icon: Crown,
      primary: false,
    },
    {
      key: 'help',
      label: 'Help & FAQ',
      sublabel: 'Get support',
      href: '/help',
      icon: HelpCircle,
      primary: false,
    },
    {
      key: 'safety',
      label: 'Safety',
      sublabel: 'Community guidelines',
      href: '/safety',
      icon: Shield,
      primary: false,
    },
  ];

  const handleDismissPremiumPopup = () => {
    dismissPremiumPopupForDay();
    setShowPremiumPopup(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNav activePage="home" />

      {/* Email verified success banner */}
      {showEmailVerifiedBanner && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-4">
          <div className="bg-green-500/15 border border-green-500/30 rounded-xl px-4 py-3 flex items-center gap-3 shadow-lg">
            <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-green-700 dark:text-green-400">Email Verified!</p>
              <p className="text-xs text-muted-foreground">Your email has been confirmed. Welcome to JSN!</p>
            </div>
            <button onClick={() => setShowEmailVerifiedBanner(false)} className="text-muted-foreground hover:text-foreground transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Welcome hero */}
      <section className="pt-[88px] pb-12 bg-surface border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 2xl:px-16">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold tracking-widest uppercase text-accent mb-3">
              Member Home
            </div>
            <h1 className="text-3xl lg:text-4xl font-black text-foreground leading-tight mb-3">
              Welcome back,{' '}
              <span className="text-gradient-red">{displayName}</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8 max-w-xl">
              You&apos;re part of the Jamuhuri Singles Network — a values-based matchmaking and singles network for Kenyan diaspora members across the USA. Explore member profiles, build connections, and find your person.
            </p>

            {/* Primary CTA */}
            <Link
              href="/discover-page"
              className="btn-primary inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-semibold"
            >
              <Compass size={17} />
              Browse Profiles / Discover
              <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* Quick navigation links */}
      <section className="py-10 bg-background border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 2xl:px-16">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-5">
            Quick Access
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.key}
                  href={link.href}
                  className={`flex flex-col items-center gap-2 p-4 rounded-2xl border text-center transition-all duration-200 hover:-translate-y-0.5 ${
                    link.primary
                      ? 'bg-primary/10 border-primary/30 hover:bg-primary/20' :'bg-card border-border hover:border-primary/40 hover:bg-muted/30'
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      link.primary ? 'bg-primary' : 'bg-muted/50'
                    }`}
                  >
                    <Icon
                      size={19}
                      className={link.primary ? 'text-white' : 'text-muted-foreground'}
                    />
                  </div>
                  <div>
                    <div
                      className={`text-xs font-semibold ${
                        link.primary ? 'text-primary' : 'text-foreground'
                      }`}
                    >
                      {link.label}
                    </div>
                    <div className="text-xs text-muted-foreground leading-tight mt-0.5 hidden sm:block">
                      {link.sublabel}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How JSN Works */}
      <HowItWorks />
    
      {/* Trust & Safety */}
      <SafetySection />

      {/* Safety Reminder + Footer */}
      <SafetyAndFooter />

      {/* Photo Rejection Modal — shown once on login when photo is rejected */}
      {showRejectionModal && rejectionReason && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md space-y-5 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={18} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground">Your Profile Photo Was Not Approved</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Action required — please upload a replacement.</p>
                </div>
              </div>
              <button onClick={() => setShowRejectionModal(false)} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0">
                <X size={16} />
              </button>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed">
              Your submitted profile photo was not approved. Please upload a replacement photo that meets JSN&apos;s photo standards.
            </p>

            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-primary">Reason: {rejectionReason}</p>
              <p className="text-sm text-foreground leading-relaxed">{memberMessage}</p>
            </div>

            <p className="text-xs text-muted-foreground font-medium">Upload a replacement photo and submit it for review.</p>

            <div className="flex gap-3">
              <button onClick={() => setShowRejectionModal(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-muted/20 border border-border text-muted-foreground hover:text-foreground transition-colors">
                Dismiss
              </button>
              <Link
                href="/account-settings"
                onClick={() => setShowRejectionModal(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold btn-primary flex items-center justify-center gap-2 text-center"
              >
                <Upload size={14} />Go to Photo Upload
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Premium Upgrade Pop-up — shown once per day to eligible free members */}
      {showPremiumPopup && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
          <div className="bg-[#1e1e1e] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            {/* Top accent bar */}
            <div className="h-1 w-full bg-gradient-to-r from-[#750000] via-[#FFBF00] to-[#008000]" />

            <div className="p-6 space-y-4">
              {/* Header row */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#750000]/20 border border-[#750000]/30 flex items-center justify-center flex-shrink-0">
                    <Crown size={18} className="text-[#FFBF00]" />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-[#FFBF00]">
                      Premium Access
                    </p>
                    <h3 className="text-base font-black text-white leading-tight">
                      Don&apos;t Wait for the Perfect Moment
                    </h3>
                  </div>
                </div>
                <button
                  onClick={handleDismissPremiumPopup}
                  aria-label="Dismiss"
                  className="p-1.5 text-[#a0a0a0] hover:text-white transition-colors flex-shrink-0 rounded-lg hover:bg-white/10"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <p className="text-sm text-[#a0a0a0] leading-relaxed">
                If someone on JSN catches your eye, don&apos;t leave the connection sitting there.{' '}
                <span className="text-white font-medium">Premium lets you unlock messaging</span> and start a real conversation.
              </p>

              {/* Divider */}
              <div className="border-t border-white/8" />

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/membership"
                  onClick={() => setShowPremiumPopup(false)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-5 rounded-xl text-sm font-bold text-white transition-all duration-200 hover:opacity-90 active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #750000 0%, #a31911 100%)' }}
                >
                  <Sparkles size={15} />
                  Upgrade to Premium
                </Link>
                <button
                  onClick={handleDismissPremiumPopup}
                  className="flex-1 py-3 px-5 rounded-xl text-sm font-semibold text-[#a0a0a0] border border-white/10 hover:border-white/20 hover:text-white transition-all duration-200 bg-white/5 hover:bg-white/10"
                >
                  Maybe Later
                </button>
              </div>

              {/* Footer note */}
              <p className="text-center text-[11px] text-[#a0a0a0]">
                Browse freely.{' '}
                <span className="text-[#008000] font-medium">Message with Premium.</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
